/**
 * SessionTimeoutModal — idle-warning dialog (VH-119, VH-284).
 *
 * Mounted inside the authenticated app shell (Layout), so it is never active
 * on public/capture routes. It wires `useIdleTimeout` to the auth layer:
 * - After 15 min of inactivity in every tab a modal appears with a live
 *   2-minute countdown, in all tabs together.
 * - "Stay logged in" refreshes the access token, resets the shared idle
 *   clock (closing the warning in every tab) so work is never lost.
 * - "Log out now", or the countdown reaching 0, ends the session on the
 *   server; every tab goes to the login screen with the idle note.
 * - VH-284: while someone is active, at most once a minute, the page asks
 *   GET /auth/me — the "still here" that keeps the server's own end (20
 *   minutes after the last request) from coming before this countdown. Its
 *   failure is nobody's business: a session that ended is found by the
 *   request itself.
 *
 * Durations are props so tests can shorten them.
 */
import { Fragment, useCallback, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ClockIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { useIdleTimeout, IDLE_TIMEOUT_MS, WARNING_DURATION_MS } from '../hooks/useIdleTimeout'
import { authAPI, refreshAccessToken } from '../services/api'

// The server moves "last active" at most once a minute; telling it more
// often buys nothing. As FinanceHub and HubPlatform (HP-21).
export const STILL_HERE_EVERY_MS = 60_000

export default function SessionTimeoutModal({
  idleTimeoutMs = IDLE_TIMEOUT_MS,
  warningDurationMs = WARNING_DURATION_MS,
  enabled = true,
}) {
  const { t } = useTranslation()
  const { sessionExpired, endAfterIdle } = useAuth()
  const [extending, setExtending] = useState(false)

  const handleExpire = useCallback(() => {
    // Countdown reached 0 with no action: end the session on the server.
    endAfterIdle()
  }, [endAfterIdle])

  // When the server was last told. The first activity (the page opening,
  // which asks the server anyway) only starts the minute.
  const toldRef = useRef(0)
  const stillHere = useCallback((at) => {
    if (!toldRef.current) {
      toldRef.current = at
      return
    }
    if (at - toldRef.current < STILL_HERE_EVERY_MS) return
    toldRef.current = at
    authAPI.getProfile().catch(() => {})
  }, [])

  const { warningActive, secondsRemaining, resetTimer } = useIdleTimeout({
    idleTimeoutMs,
    warningDurationMs,
    enabled,
    onActivity: stillHere,
    onExpire: handleExpire,
  })

  const handleExtend = async () => {
    if (extending) return
    setExtending(true)
    try {
      await refreshAccessToken()
      resetTimer()
    } catch {
      // Refresh token gone/expired — end the session cleanly.
      sessionExpired()
    } finally {
      setExtending(false)
    }
  }

  const handleLogout = () => {
    endAfterIdle()
  }

  return (
    <Transition show={warningActive} as={Fragment}>
      {/* onClose is a no-op: the user must choose Extend or Log out. */}
      <Dialog as="div" className="relative z-[70]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-xl bg-surface-container p-6 shadow-xl">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
                  <ClockIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {t('session.expiryWarningTitle')}
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-gray-600">
                    {t('session.expiryWarningMessage')}
                  </p>
                  <p
                    className="mt-3 text-sm font-medium text-gray-900"
                    role="timer"
                    aria-live="assertive"
                  >
                    {t('session.expiresIn', { seconds: secondsRemaining })}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-surface-container px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  {t('session.logoutNow')}
                </button>
                <button
                  type="button"
                  onClick={handleExtend}
                  disabled={extending}
                  className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
                >
                  {t('session.extend')}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
